/* eslint-disable no-nested-ternary */

import { put, select, takeLatest } from 'redux-saga/effects';
import { AppTableInitialState, createAppTableSaga, createAppTableStore, createPersist } from 'components/AppTable/store';
import apiRoutes from 'config/apiRoute';
import { convertMinsToHrsMins } from 'utils/string.utils';

export const requestSlice = createAppTableStore({
  name: 'tableRequests' as const,
  url: apiRoutes.REQUESTS,
  initialState: {
    params: {
      filters: {
        status: [
          { value: 'new' },
          { value: 'waiting_approval' },
          { value: 'approved' },
          { value: 'needs_attention' },
          { value: 'in_progress' },
        ],
        'projects_requests.temperature': [{ value: 1 }, { value: 2 }, { value: 3 }],
        'user.uuid': [],
        'project.plan': [
          { value: 'starter' },
          { value: 'premium' },
          { value: 'enterprise' },
          { value: 'custom' },
          { value: 'hosting' },
        ],
      },
      sort: { column: 'created_at', order: 'descend' },
    },
    paramFilterFields: {
      status: 'filters[projects_requests.status][]',
      'project.plan': 'filters[project.plan][]',
      billable: 'filters[billable][]',
      overdue_only: 'overdue_only',
    },
    searchFilters: {
      created_at_start: { rule: 'greater_than_or_equals', field: 'projects_requests.created_at', index: 1 },
      created_at_end: { rule: 'less_than_or_equals', field: 'projects_requests.created_at', index: 2 },
      target_date_start: { rule: 'greater_than_or_equals', field: 'projects_requests.target_date', index: 3 },
      target_date_end: { rule: 'less_than_or_equals', field: 'projects_requests.target_date', index: 4 },
    },
    searchField: '*',
    searchKey: 'search_term[0][0]',
    fixedParams: {
      with_total_time: true,
      with_count_per_temp: true,
      with: 'latest_comment',
    },
    totalTime: null as null | string,
    countPerTemp: null as null | { cold: number; warm: number; hot: number },
    apiParamsHandler(params, state) {
      const newParams = { ...params };

      delete newParams['filters[project_type]'];

      const hasProjectPlanParam =
        newParams['filters[project.plan][]'] || newParams['filters[project.plan]'] || newParams['filters[project.plan][]'];

      if (!hasProjectPlanParam) {
        const planFilter = state?.params?.filters?.['project.plan'];
        if (planFilter && Array.isArray(planFilter) && planFilter.length > 0) {
          newParams['filters[project.plan][]'] = planFilter.map((p) => p.value);
        }
      }

      const temp = newParams['filters[projects_requests.temperature]'];
      if (Array.isArray(temp) && temp.length === 3) {
        delete newParams['filters[projects_requests.temperature]'];
      }

      if (newParams['filters[related_users._shared_with_me]']) {
        const raw = newParams['filters[related_users._shared_with_me]'];
        const arr = Array.isArray(raw) ? raw.map(String) : [String(raw)];
        const existing = newParams['filters[related_users.uuid][]'];
        const existingArr = Array.isArray(existing) ? existing.map(String) : existing ? [String(existing)] : [];
        newParams['filters[related_users.uuid][]'] = [...existingArr, ...arr];
        delete newParams['filters[related_users._shared_with_me]'];
      }

      return newParams;
    },
  },
  reducers: {
    setTotalTime: (state, { payload }: { payload: string; type: string }) => {
      return {
        ...state,
        totalTime: payload,
      } as any;
    },
    setCountPerTemp: (state, { payload }: { payload: any; type: string }) => {
      return {
        ...state,
        countPerTemp: payload,
      } as any;
    },
  },
});

function* handleApiResponseSagaListener({ payload }: { payload: any }) {
  yield put(requestSlice.actions.setTotalTime(convertMinsToHrsMins(Number(payload.total_time))));
  yield put(requestSlice.actions.setCountPerTemp(payload.count_per_temp));
}

function* onRehydrate() {
  const store: AppTableInitialState = yield select((globalStore) => globalStore[requestSlice.name] as AppTableInitialState);

  const hasPlan =
    store?.params?.filters &&
    store.params.filters['project.plan'] &&
    Array.isArray(store.params.filters['project.plan']) &&
    store.params.filters['project.plan'].length > 0;

  if (!hasPlan) {
    yield put(
      requestSlice.actions.setFilter({
        'project.plan': [
          { value: 'starter' },
          { value: 'premium' },
          { value: 'enterprise' },
          { value: 'custom' },
          { value: 'hosting' },
        ],
      }),
    );
  }
}

export const requestsSaga = createAppTableSaga(requestSlice, function* customGenerator() {
  yield takeLatest(requestSlice.actions.handleApiResponse, handleApiResponseSagaListener);
  yield takeLatest('persist/REHYDRATE', onRehydrate);
});

export const requestsReducer = createPersist(requestSlice);
